import type { AccountingCustomersUpdateRequest } from '../../../types/models/AccountingCustomer';
import type { AccountingEntriesCustomer } from '../../../types/models/Reconciliation';

// The « Codes clients WinAuditor » form, over the entries view's own `customers[]` (the customers the
// month's ODs name): a code per Facturier client (`accounting_key`), client-side validation mirroring the
// backend's rule (it re-validates and is the authority), and the PATCH body — only the codes that changed.
// A code stays a STRING end to end: its leading zeros are significant (`0000004`).

/** Draft codes keyed by `accounting_key`; a customer without one has no Facturier client and no input. */
export type CustomerCodesDraft = Record<string, string>;

const CODE_RE = /^[A-Za-z0-9]{1,10}$/;

/** The customers a code can be recorded for (they have a Facturier client). */
export const editableCustomers = (customers: AccountingEntriesCustomer[]) =>
  customers.filter((c): c is AccountingEntriesCustomer & { accounting_key: number } => c.accounting_key !== null);

export function customerCodesDraftFrom(customers: AccountingEntriesCustomer[]): CustomerCodesDraft {
  const draft: CustomerCodesDraft = {};
  for (const c of editableCustomers(customers)) {
    draft[String(c.accounting_key)] = c.winauditor_code ?? '';
  }
  return draft;
}

/** The French error of a code, or null when valid (trimmed, 1–10 digits / letters; empty = no code). */
export function customerCodeError(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' || CODE_RE.test(trimmed)
    ? null
    : 'Le code doit compter de 1 à 10 chiffres ou lettres (par exemple 0000004).';
}

export function isCustomerCodesDraftValid(draft: CustomerCodesDraft): boolean {
  return Object.values(draft).every((v) => customerCodeError(v) === null);
}

/**
 * The PATCH body with only the changed codes, or null when nothing changed: a new or edited code is sent
 * trimmed, a cleared one as `null` (forget it); an untouched empty code is not sent.
 */
export function buildCustomerCodesPatch(
  customers: AccountingEntriesCustomer[],
  draft: CustomerCodesDraft,
): AccountingCustomersUpdateRequest | null {
  const changes: AccountingCustomersUpdateRequest['customers'] = {};
  for (const c of editableCustomers(customers)) {
    const key = String(c.accounting_key);
    const value = draft[key];
    if (value === undefined) {
      continue;
    }
    const code = value.trim();
    if (code !== (c.winauditor_code ?? '')) {
      changes[key] = code === '' ? null : code;
    }
  }
  return Object.keys(changes).length === 0 ? null : { customers: changes };
}

/** How many of the month's customers still lack a code. */
export const missingCodeCount = (customers: AccountingEntriesCustomer[]): number =>
  customers.filter((c) => c.winauditor_code === null).length;
